import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getUserSettings, updateUserSettings, extractErrorMessage } from '../services/api';
import { useToast } from '../components/Toast';
import { FaCog, FaSave } from 'react-icons/fa';
import '../styles/Settings.css';

function Settings({ user }) {
  const toast = useToast();
  const [settings, setSettings] = useState({
    timezone: 'Europe/Berlin',
    language: 'de',
    notifications_enabled: true,
    privacy_mode: false,
    safe_word: '',
    theme: 'dark',
    display_name: '',
    bio: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await getUserSettings();
      if (data && typeof data === 'object') {
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error(extractErrorMessage(error, 'Einstellungen konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await updateUserSettings(settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      toast.success('Einstellungen gespeichert');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Speichern der Einstellungen'));
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  if (loading) {
    return (
      <div className="app-layout">
        <Navbar user={user} />
        <main className="main-content">
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1><FaCog /> Einstellungen</h1>
        </div>

        <form className="settings-form" onSubmit={handleSave}>
          <div className="settings-section">
            <h3 className="section-title">Profil</h3>
            <div className="form-group">
              <label>Anzeigename</label>
              <input
                type="text"
                value={settings.display_name}
                onChange={e => handleChange('display_name', e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div className="form-group">
              <label>Bio</label>
              <textarea
                value={settings.bio}
                onChange={e => handleChange('bio', e.target.value)}
                placeholder="Erzähle etwas über dich..."
              />
            </div>
          </div>

          <div className="settings-section">
            <h3 className="section-title">Allgemein</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Zeitzone</label>
                <select
                  value={settings.timezone}
                  onChange={e => handleChange('timezone', e.target.value)}
                >
                  <option value="Europe/Berlin">Europe/Berlin (CET)</option>
                  <option value="Europe/London">Europe/London (GMT)</option>
                  <option value="America/New_York">America/New_York (EST)</option>
                  <option value="America/Los_Angeles">America/Los_Angeles (PST)</option>
                  <option value="America/Chicago">America/Chicago (CST)</option>
                  <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                  <option value="Australia/Sydney">Australia/Sydney (AEST)</option>
                  <option value="UTC">UTC</option>
                </select>
              </div>
              <div className="form-group">
                <label>Sprache</label>
                <select
                  value={settings.language}
                  onChange={e => handleChange('language', e.target.value)}
                >
                  <option value="de">Deutsch</option>
                  <option value="en">English</option>
                </select>
              </div>
            </div>
          </div>

          <div className="settings-section">
            <h3 className="section-title">Benachrichtigungen</h3>
            <div className="toggle-group">
              <div className="toggle-row">
                <div className="toggle-info">
                  <span className="toggle-label">Benachrichtigungen aktiviert</span>
                  <span className="toggle-desc">Erhalte Benachrichtigungen über neue Aufgaben, Verträge und mehr</span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings.notifications_enabled}
                    onChange={e => handleChange('notifications_enabled', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>

          <div className="settings-section">
            <h3 className="section-title">Privatsphäre & Sicherheit</h3>
            <div className="toggle-group">
              <div className="toggle-row">
                <div className="toggle-info">
                  <span className="toggle-label">Privatsphäre-Modus</span>
                  <span className="toggle-desc">Verstecke sensible Informationen in der Übersicht</span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings.privacy_mode}
                    onChange={e => handleChange('privacy_mode', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
            <div className="form-group safe-word-group">
              <label>Safe Word</label>
              <input
                type="text"
                value={settings.safe_word}
                onChange={e => handleChange('safe_word', e.target.value)}
                placeholder="Dein persönliches Safe Word"
                className="safe-word-input"
              />
              <span className="field-hint">Wird für Notfall-Stops in allen Kontexten verwendet</span>
            </div>
          </div>

          <div className="settings-actions">
            <button type="submit" className="save-btn" disabled={saving}>
              <FaSave /> {saving ? 'Speichert...' : 'Einstellungen speichern'}
            </button>
            {saved && <span className="saved-message">Gespeichert!</span>}
          </div>
        </form>
      </main>
    </div>
  );
}

export default Settings;
